import type { AccessTokenData } from '@empathyco/playboard-core-domain'
import type { AuthTokenService } from './interfaces'

export interface JsonWebTokenPayload {
  sub: string
  exp: number
}

export abstract class JsonWebTokenService<PayloadType extends JsonWebTokenPayload>
  implements AuthTokenService
{
  getInfo(token: string): AccessTokenData {
    const payload = this.decodePayload(token)
    const authorization = this.getAuthorizationFromPayload(payload)
    return {
      user: this.getUserInfoFromPayload(payload),
      authorization,
      expirationDate: this.getExpirationDateFromPayload(payload),
    }
  }

  isExpired(token: string): boolean {
    const payload = this.decodePayload(token)
    const expirationDate = this.getExpirationDateFromPayload(payload)
    return expirationDate.getTime() <= Date.now()
  }

  isNearToExpire(token: string, marginInMillis: number): boolean {
    const payload = this.decodePayload(token)
    const expirationDate = this.getExpirationDateFromPayload(payload)
    const expirationDateInMillis = expirationDate.getTime()
    const now = Date.now()
    return expirationDateInMillis > now && expirationDateInMillis < now + marginInMillis
  }

  protected abstract getUserInfoFromPayload(payload: PayloadType): AccessTokenData['user']

  protected abstract getAuthorizationFromPayload(
    payload: PayloadType,
  ): AccessTokenData['authorization']

  private decodePayload(token: string): PayloadType {
    // https://stackoverflow.com/a/38552302
    const base64Payload = token.split('.')[1].replace('-', '+').replace('_', '/')
    const jsonPayload = decodeURIComponent(
      atob(base64Payload)
        .split('')
        .map((char: string) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(''),
    )
    return JSON.parse(jsonPayload)
  }

  private getExpirationDateFromPayload(payload: PayloadType): Date {
    return new Date(payload.exp * 1000)
  }
}
